
import React, { useState } from 'react';
import { ApprovalFlow, ApprovalStage } from '../../types/approval';
import { useAuth } from '../../context/AuthContext';
import { isLegal, isLisa, isIrene } from '../../config/roles';
import { FileUploadKit } from '../common/FileUploadKit';
import { uploadApprovalFile, updateApprovalRequest } from '../../services/approvalRequestsService';
import type { AttachmentInfo } from '../../types/legalOps';
import { useToast } from '../Toast';

// [Synced] Approval Engine v4.6.1

interface Props {
  flow: ApprovalFlow | null;
  onDecision?: (stageKey: string, decision: 'APPROVED' | 'REJECTED' | 'RETURNED' | 'DONE', comment: string, deptCode?: string) => void;
  requestId?: string;
  reviewAttachments?: AttachmentInfo[];
  kcAttachments?: AttachmentInfo[];
  onKcAttachmentsChange?: (files: AttachmentInfo[]) => void;
  onReviewAttachmentsChange?: (files: AttachmentInfo[]) => void;
}

const ALLOWED_EDITORS = ['kc@lg.app', 'lisa@lg.app', 'irene@lg.app'];

export const ApprovalFlowPanel: React.FC<Props> = ({ 
    flow, 
    onDecision, 
    requestId, 
    reviewAttachments = [], 
    kcAttachments = [], 
    onKcAttachmentsChange 
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [commentMap, setCommentMap] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [editingStageKey, setEditingStageKey] = useState<string | null>(null);

  if (!flow) {
    return (
      <div style={{padding: '24px', border: '1px dashed #444', borderRadius: '8px', color: '#888', textAlign: 'center', fontSize: '13px', background: 'rgba(255,255,255,0.02)'}}>
        <div style={{marginBottom: 8, fontSize: 16}}>📋 簽核流程</div>
        <div>尚未啟用簽核流程。</div>
        <div style={{fontSize: 12, marginTop: 4, opacity: 0.7}}>（可於編輯或送出時啟用）</div>
      </div>
    );
  }

  // --- LEGACY DETECTION ---
  const isLegacy = (flow.stages && flow.stages.length > 0 && !flow.stages[0].key) || (!flow.currentStageKey && flow.status === 'IN_PROGRESS');

  if (isLegacy) {
      return (
          <div className="section" style={{background: 'rgba(255, 90, 90, 0.1)', padding: '16px', border: '1px solid #ff5a6a', borderRadius: 12}}>
             <div style={{color: '#ff5a6a', fontWeight: 'bold', marginBottom: 10, fontSize: 16}}>⚠️ 偵測到舊版簽核資料 (Legacy Flow)</div>
             <div style={{color: '#e5e7eb', fontSize: 14, lineHeight: 1.5, marginBottom: 12}}>
                 此單據為舊版資料格式，為避免系統錯誤，已暫停操作功能。<br/>
                 請聯繫系統管理員進行修復 (Admin Only)。
             </div>
          </div>
      );
  }

  const getStatusColor = (status: string) => {
      switch(status) {
          case 'APPROVED': case 'COMPLETED': case 'DONE': return 'var(--ok)';
          case 'REJECTED': case 'RETURNED': return 'var(--danger)';
          case 'PENDING': return '#fbbf24'; 
          case 'NOT_STARTED': return 'var(--muted)';
          default: return 'var(--muted)';
      }
  };

  const getStatusLabel = (status: string) => {
      switch(status) {
          case 'APPROVED': return '已核准';
          case 'COMPLETED': return '流程完成';
          case 'REJECTED': return '已駁回';
          case 'RETURNED': return '退回';
          case 'PENDING': return '待審核';
          case 'DONE': return '已會辦';
          case 'NOT_STARTED': return '未開始';
          case 'IN_PROGRESS': return '進行中';
          default: return status;
      }
  };

  const getCommentLabel = (stageKey: string, role: string) => {
      if (stageKey === 'LEGAL_REVIEW' || role === 'legal') return "法務意見";
      if (role === 'multi-dept') return "會辦意見";
      return "核決意見";
  };

  const canActOn = (stage: ApprovalStage) => {
      if (flow.currentStageKey !== stage.key) return false;
      if (stage.status !== 'PENDING') return false;

      const email = user?.email;
      if (stage.key === 'CO_REVIEW') return true; 
      if (stage.approverRole === 'legal') return isLegal(email);
      if (stage.approverRole === 'lisa') return isLisa(email);
      if (stage.approverRole === 'irene') return isIrene(email);
      return false;
  };

  const canEditDecision = (stage: ApprovalStage) => {
      if (!user?.email) return false;
      if (stage.status === 'PENDING' || stage.status === 'NOT_STARTED') return false;
      
      const email = user.email.toLowerCase();
      if (!ALLOWED_EDITORS.includes(email)) return false;

      if (stage.approverRole === 'legal' && isLegal(email)) return true;
      if (stage.approverRole === 'lisa' && isLisa(email)) return true;
      if (stage.approverRole === 'irene' && isIrene(email)) return true;
      
      return false;
  };

  const handleAction = async (stageKey: string, decision: 'APPROVED'|'REJECTED'|'RETURNED'|'DONE') => {
      if (!commentMap[stageKey] && (decision === 'REJECTED' || decision === 'RETURNED')) {
          return alert("請填寫意見 (駁回/退回必填)");
      }
      
      if (isEditingThisStage && !confirm(`確定改為${decision}?`)) return;

      if (onDecision) {
          try {
              // Wrap call to handle potential errors from service
              await onDecision(stageKey, decision, commentMap[stageKey] || "", undefined);
              if(isEditingThisStage) cancelEditing();
          } catch(e: any) {
              // Error is displayed by showToast via parent usually, 
              // but here we can catch it if parent throws
              console.error(e);
              showToast(`操作失敗: ${e.message}`, "error");
          }
      }
  };

  const isEditingThisStage = (stageKey: string) => editingStageKey === stageKey;

  const handleKcFileUpload = async (files: File[]) => {
      if (!requestId || !user?.email) return;
      setUploading(true);
      try {
          const newAtts: AttachmentInfo[] = [];
          for (const f of files) {
              const meta = await uploadApprovalFile(requestId, 'review', f, user.email);
              newAtts.push(meta);
          }
          const updated = [...(kcAttachments || []), ...newAtts];
          await updateApprovalRequest(requestId, { kcAttachments: updated });
          if (onKcAttachmentsChange) onKcAttachmentsChange(updated);
          showToast("法務審查附件已上傳");
      } catch (e) {
          console.error(e);
          showToast("上傳失敗", "error");
      } finally {
          setUploading(false);
      }
  };

  const handleRemoveKcFile = async (idx: number) => {
      if (!requestId) return;
      if (!confirm("確定刪除此附件？")) return;
      const updated = [...(kcAttachments || [])];
      updated.splice(idx, 1);
      try {
          await updateApprovalRequest(requestId, { kcAttachments: updated });
          if (onKcAttachmentsChange) onKcAttachmentsChange(updated);
          showToast("附件已刪除");
      } catch(e) {
          showToast("刪除失敗", "error");
      }
  };

  const startEditing = (stage: ApprovalStage) => {
      setEditingStageKey(stage.key);
      setCommentMap({ ...commentMap, [stage.key]: stage.comment || '' });
  };
  
  const cancelEditing = () => {
      setEditingStageKey(null);
  };

  const isLegalReview = (stageKey: string) => stageKey === 'LEGAL_REVIEW';
  const isLegalUser = isLegal(user?.email);

  return (
    <div className="section" style={{background: '#1a1d24', padding: '16px', border: '1px solid var(--border)', borderRadius: 12}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #333'}}>
        <div style={{fontWeight: 'bold', color: '#fff', fontSize: 15}}>
            簽核流程 
            <span style={{fontSize:12, fontWeight:'normal', color:'#aaa', marginLeft:8}}>
                ID: {flow.id}
            </span>
        </div>
        <div className="badge" style={{borderColor: getStatusColor(flow.status), color: getStatusColor(flow.status)}}>
          {getStatusLabel(flow.status)}
        </div>
      </div>

      <div style={{display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative'}}>
        <div style={{position: 'absolute', left: '20px', top: '20px', bottom: '20px', width: '2px', background: '#333', zIndex: 0}}></div>

        {flow.stages.map((stage, idx) => {
          const isCurrent = flow.currentStageKey === stage.key;
          const statusColor = getStatusColor(stage.status);
          const isDone = stage.status === 'APPROVED' || stage.status === 'REJECTED' || stage.status === 'RETURNED';
          
          const commentLabel = getCommentLabel(stage.key, stage.approverRole);
          const userCanEdit = canEditDecision(stage);
          const userCanAct = canActOn(stage);

          return (
            <div key={stage.key || idx} style={{display: 'flex', gap: '12px', zIndex: 1, opacity: (stage.status === 'NOT_STARTED' && !isCurrent) ? 0.5 : 1}}>
              <div style={{
                  width: '42px', height: '42px', borderRadius: '50%', 
                  background: isCurrent ? '#2563eb' : (isDone ? '#10b981' : '#2a2a2a'),
                  border: `2px solid ${isCurrent ? '#60a5fa' : (isDone ? '#10b981' : '#444')}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 'bold', fontSize: '14px', color: '#fff', flexShrink: 0
              }}>
                  {idx + 1}
              </div>

              <div style={{
                  flex: 1,
                  borderRadius: '8px',
                  border: `1px solid ${isCurrent ? '#60a5fa' : '#333'}`,
                  background: isCurrent ? 'rgba(37, 99, 235, 0.05)' : '#222',
                  padding: '12px'
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px'}}>
                  <div style={{fontWeight: 500, fontSize: '14px', color: isCurrent ? '#60a5fa' : '#e5e7eb'}}>
                    {stage.label || `Stage ${idx+1}`}
                  </div>
                  <div style={{display: 'flex', alignItems:'center', gap: 8}}>
                      <div style={{fontSize: '12px', color: statusColor, fontWeight: 'bold'}}>
                        {getStatusLabel(stage.status)}
                      </div>
                      
                      {userCanEdit && !isEditingThisStage(stage.key) && (
                          <button 
                            className="smallbtn" 
                            style={{fontSize: 10, padding: '2px 6px', background: 'transparent', border: '1px solid #555', color: '#ccc'}}
                            onClick={() => startEditing(stage)}
                          >
                            ✎ 修改
                          </button>
                      )}
                  </div>
                </div>

                {(stage.approverName || stage.actedAt) && (
                    <div style={{fontSize: '11px', color: '#888', marginBottom: '8px'}}>
                        {stage.approverName || '簽核人'} • {stage.actedAt ? new Date(stage.actedAt).toLocaleString() : ''}
                    </div>
                )}

                {/* Comment Display */}
                {!isEditingThisStage(stage.key) && stage.comment && (
                  <div style={{marginTop: '10px'}}>
                    <div style={{fontSize: '12px', color: '#a5b4fc', marginBottom: '4px', fontWeight: 'bold'}}>
                        {commentLabel}
                    </div>
                    <div style={{fontSize: '13px', color: '#ddd', padding: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', whiteSpace: 'pre-wrap', borderLeft: '3px solid #666'}}>
                        {stage.comment}
                    </div>
                  </div>
                )}

                {/* KC Attachment Uploader */}
                {isLegalReview(stage.key) && isLegalUser && isCurrent && (
                     <div style={{marginTop: 12, borderTop: '1px dashed #555', paddingTop: 8, background: 'rgba(0,0,0,0.2)', padding: 8, borderRadius: 6}}>
                        <FileUploadKit 
                            label="上傳法務審查附件"
                            existingFiles={kcAttachments}
                            onFilesSelected={handleKcFileUpload}
                            onRemoveFile={(f, i) => handleRemoveKcFile(i)}
                            disabled={uploading}
                        />
                        {uploading && <div style={{fontSize:11, color:'#fbbf24'}}>上傳中...</div>}
                    </div>
                )}

                {/* Editable Area */}
                {(userCanAct || isEditingThisStage(stage.key)) && onDecision && (
                  <div style={{marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed rgba(255,255,255,0.1)'}}>
                    <label className="label" style={{color: '#a5b4fc', marginBottom: '6px', display:'block', fontWeight: 'bold'}}>
                        {commentLabel} {['REJECTED', 'RETURNED'].includes(stage.status) && <span style={{color: 'var(--danger)'}}>*</span>}
                    </label>
                    
                    <textarea
                      className="input"
                      placeholder="請輸入意見..."
                      rows={4}
                      value={commentMap[stage.key] ?? ''}
                      onChange={(e) => setCommentMap({...commentMap, [stage.key]: e.target.value})}
                      style={{width: '100%', marginBottom: '8px', fontSize: '13px', minHeight: '80px', background: '#0b0b0c'}}
                    />

                    <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                      {isEditingThisStage(stage.key) && (
                          <button className="smallbtn" style={{background: 'transparent', border:'1px solid #666', color:'#ccc'}} onClick={cancelEditing}>取消</button>
                      )}
                      
                      <button
                        className="smallbtn"
                        style={{color: 'var(--danger)', borderColor: 'var(--danger)'}}
                        onClick={() => handleAction(stage.key, 'REJECTED')}
                      >
                        駁回
                      </button>

                      {isLegalReview(stage.key) && (
                          <button
                            className="smallbtn"
                            style={{color: '#fbbf24', borderColor: '#fbbf24'}}
                            onClick={() => handleAction(stage.key, 'RETURNED')}
                          >
                            退回
                          </button>
                      )}
                      
                      <button
                        className="smallbtn"
                        style={{background: 'var(--ok)', color: '#000', border: 'none'}}
                        onClick={() => handleAction(stage.key, 'APPROVED')}
                      >
                        {isEditingThisStage(stage.key) ? "儲存修改" : "核准 / 送出"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
